import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogTitle } from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { assets } from '@/lib/assets';
import { cn } from '@/lib/utils';
import { gameDataContext, GameOrder } from '@/providers/GameDataProvider';
import { Diamond } from '../Diamond';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * A popup component that displays the game progression as a timeline
 * Each game in the GameOrder array is represented by a diamond icon showing the score
 */
export const ProgressPopup = ({ isOpen, onClose }: Props) => {
  const { currentGameIndex, gameScores, totalScore } =
    useContext(gameDataContext);
  const { t } = useTranslation();

  // Helper function to get the translation key for a game path
  const getGameNameKey = (gamePath: string) => {
    if (gamePath.includes('game_lights')) {
      return 'game_lights.title';
    } else if (gamePath.includes('game_heater')) {
      return 'game_heater.title';
    } else if (gamePath.includes('game_quizz_01')) {
      return 'game_quizz_1.title';
    } else if (gamePath.includes('game_quizz_02')) {
      return 'game_quizz_2.title';
    }
    return '';
  };

  return (
    <Dialog
      open={isOpen}
      defaultOpen={false}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="w-11/12 max-w-[600px] bg-primary py-10 text-center sm:w-full md:gap-4"
        // prevent close on click outside
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <Diamond
          value={totalScore}
          className={cn('-mb-5 -mt-16 h-20', 'md:-mt-18 md:h-20')}
        />
        <DialogTitle
          className={cn(
            'pt-3 text-3xl font-bold',
            'md:pt-4 md:text-4xl md:leading-tight'
          )}
        >
          {t('popup.gameProgress.title')}
        </DialogTitle>
        {/* <h2 className="select-none text-2xl font-bold">
          {t('popup.gameProgress.title')}
        </h2> */}
        <DialogDescription className="mb-6 select-none text-lg md:text-xl">
          {t('popup.gameProgress.description', {
            num: currentGameIndex,
            totalScore: totalScore,
          })}
        </DialogDescription>
        <div className="flex w-full flex-col items-center justify-center gap-6">
          {/* Create a visual timeline with game images */}
          <div className="relative flex w-full max-w-md items-center justify-between">
            {/* Timeline line */}
            <div className="absolute left-[10%] top-8 h-1 w-[80%] bg-gray-400"></div>
            {/* Timeline line progress */}
            <div
              className="absolute left-[10%] top-8 h-1 bg-secondary"
              style={{
                width: `${(currentGameIndex / (GameOrder.length - 1)) * 80}%`,
              }}
            />

            {/* Game icons on the timeline */}
            {GameOrder.map((gamePath, index) => {
              const isCurrent = currentGameIndex === index;
              const gameNameKey = getGameNameKey(gamePath);
              const gameScore = gameScores[index] || 0;
              const isCompleted = index < currentGameIndex;
              // const isCompleted = index < currentGameIndex || gameScore > 0;

              return (
                <div
                  key={gamePath}
                  className={cn(
                    'relative z-10 flex w-16 flex-col items-center justify-center',
                    'md:w-20'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full border-4 transition-transform',
                      isCompleted
                        ? 'border-green-500 bg-green-100'
                        : 'border-gray-300 bg-gray-100',
                      isCurrent && 'scale-125 border-secondary'
                    )}
                  >
                    <div className="relative flex flex-col items-center justify-center">
                      <img
                        src={assets.common.diamond}
                        alt={gameNameKey ? t(gameNameKey) : `Game ${index + 1}`}
                        className={cn(
                          'h-10 w-10 object-contain',
                          isCompleted || isCurrent
                            ? 'opacity-100'
                            : 'opacity-10'
                        )}
                      />

                      <div
                        className={cn(
                          'absolute bottom-2 select-none text-center text-2xl font-bold drop-shadow-[0px_1px_2px_rgba(0,0,0,0.9)]',
                          isCompleted || isCurrent
                            ? 'opacity-100'
                            : 'opacity-50'
                        )}
                      >
                        {gameScore}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 h-8 max-w-20 text-center text-xs font-medium">
                    {gameNameKey ? t(gameNameKey) : `Game ${index + 1}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <DialogFooter className="mt-5">
          <Button
            type="submit"
            className="min-w-40"
            onClick={onClose}
            variant="secondary"
          >
            {t('common.continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
